import React, { useState } from 'react';
import { Grid } from '@material-ui/core';

import type { Client } from '@klp/client/classes';
import { Heading } from '@klp/design';
import { Table, TableColumnDef } from '@klp/design/Table';
import { Search, filterSearchResult } from '@klp/search';

interface ClientSelectionTablesProps {
    clients: Array<Client>;
    onSelectClient: (client: Client) => void;
    showInactive?: boolean;
}

export const ClientSelectionTables: React.FC<ClientSelectionTablesProps> = ({
    clients,
    onSelectClient,
    showInactive = false,
}) => {
    const [search, setSearch] = useState('');

    const handleSearch = (searchValue: string) => {
        setSearch(searchValue);
    };

    const getActiveClients = (clientList: Array<Client>): Array<Client> =>
        clientList.filter((client) => (client.aktive ?? 0) > 0);

    const getFutureClients = (clientList: Array<Client>): Array<Client> =>
        clientList.filter(
            (client) =>
                (client.fremtidige ?? 0) > 0 && (client.aktive ?? 0) === 0,
        );

    const getInactiveClients = (clientList: Array<Client>): Array<Client> =>
        clientList.filter(
            (client) =>
                (client.inaktive ?? 0) >= 0 &&
                (client.aktive ?? 0) === 0 &&
                (client.fremtidige ?? 0) === 0,
        );

    const searchResult = filterSearchResult<Client>(clients, search, [
        'kundeName',
        'kundeNummer',
    ]);

    const tableDef = [
        new TableColumnDef({
            translation: 'clientSelect.clientNumber',
            key: 'kundeNummer',
            sortable: true,
            align: 'left',
        }),
        new TableColumnDef({
            translation: 'clientSelect.clientName',
            key: 'kundeName',
            sortable: true,
            align: 'left',
        }),
    ];

    const activeClients = getActiveClients(searchResult);
    const inactiveClients = getInactiveClients(searchResult);
    const futureClients = getFutureClients(searchResult);

    return (
        <>
            <Grid container={true}>
                <Grid item={true} xs={12} sm={6}>
                    <Search
                        searchHandler={handleSearch}
                        searchOnType={true}
                        placeholder="clientSelect.searchClient"
                    />
                </Grid>
            </Grid>
            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="THREE"
                    marginTop="bulky"
                    marginBottom="tiny"
                    translation="bygg.activeClients"
                />
                <Table
                    id="ClientSelectSearchResultAktive"
                    emptyTableMessage="clientSelect.notFound"
                    data={activeClients}
                    columns={tableDef}
                    highlightPhrase={search}
                    onRowSelect={onSelectClient}
                    sortBy={tableDef[1]}
                    sortByDirection="asc"
                    data-testid="ClientSelectSearchResultAktive"
                    activeFilter={[]}
                />
            </Grid>
            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="THREE"
                    marginTop="bulky"
                    marginBottom="tiny"
                    translation="bygg.futureClients"
                />
                <Table
                    id="ClientSelectSearchResultFremtidige"
                    emptyTableMessage="clientSelect.notFound"
                    data={futureClients}
                    columns={tableDef}
                    highlightPhrase={search}
                    onRowSelect={onSelectClient}
                    sortBy={tableDef[1]}
                    sortByDirection="asc"
                    data-testid="ClientSelectSearchResultFremtidige"
                    activeFilter={[]}
                />
            </Grid>
            {showInactive && (
                <Grid item={true} xs={12}>
                    <Heading
                        klpHeadingLevel="THREE"
                        marginTop="bulky"
                        marginBottom="tiny"
                        translation="bygg.inactiveClients"
                    />
                    <Table
                        id="ClientSelectSearchResultInaktive"
                        emptyTableMessage="clientSelect.notFound"
                        data={inactiveClients}
                        columns={tableDef}
                        highlightPhrase={search}
                        onRowSelect={onSelectClient}
                        sortBy={tableDef[1]}
                        sortByDirection="asc"
                        data-testid="ClientSelectSearchResultInaktive"
                        activeFilter={[]}
                    />
                </Grid>
            )}
        </>
    );
};
